import {
  FilterComponentData,
  FilterReducerAction,
  IdValuePair,
  InputFilterComponentData,
} from 'reducers/GenericFiltersReducer/GenericFiltersReducer.d'
import { Dispatch, ReactElement, cloneElement, useMemo, useState } from 'react'
import FilterSelect from './FilterSelect'
import FilterInput from './FilterInput'
import styles from './Filters.module.scss'
import clsx from 'clsx'
import FilterSelectInputCombo from './FilterSelectInputCombo'
import FilterRowSkeleton from 'components/Skeletons/FilterRowSkeleton'

interface FilterRowProps {
  filtersData: FilterComponentData[]
  reducerDispatch: Dispatch<FilterReducerAction>
  isLoading: boolean
  filtersId: string
  className?: string
  groupInputs?: boolean
  groupInputKeys?: IdValuePair[]
  permissions?: null | {
    canGetEntity: boolean
  }
  initialisation?: {
    isInitialising: boolean
    skeletonCount?: number
  }
  children?: ReactElement
}

function FilterRow({
  filtersData,
  reducerDispatch,
  isLoading,
  filtersId,
  className,
  groupInputs = false,
  groupInputKeys = [],
  permissions,
  initialisation,
  children,
}: FilterRowProps) {
  const [activeSearchElementKey, setActiveSearchElementKey] = useState<string>(groupInputKeys[0]?.id)
  const canGet = permissions ? permissions.canGetEntity : true

  const childElement = useMemo(() => {
    if (!children) return null
    return cloneElement(children, { className: styles.filter__endSlot })
  }, [children])

  const filterSelectElements = useMemo(() => {
    return filtersData.map(filterObject => {
      if (filterObject.type !== 'select') return null
      return (
        <FilterSelect
          filterObject={filterObject}
          reducerDispatch={reducerDispatch}
          isLoading={isLoading}
          key={filterObject.id}
        />
      )
    })
  }, [JSON.stringify(filtersData), reducerDispatch, isLoading])

  const filterFreetextElements = useMemo(() => {
    if (groupInputs) {
      const inputObject = filtersData.find(filter => filter.id === activeSearchElementKey) as InputFilterComponentData
      return (
        <FilterSelectInputCombo
          groupInputKeys={groupInputKeys}
          isLoading={isLoading}
          reducerDispatch={reducerDispatch}
          inputObject={inputObject}
          activeSearchElementKey={activeSearchElementKey}
          setActiveSearchElementKey={setActiveSearchElementKey}
          key="input-select-combo-container"
        />
      )
    } else {
      return filtersData.map(filterObject => {
        if (filterObject.type !== 'text') return null
        return (
          <FilterInput
            filterObject={filterObject}
            reducerDispatch={reducerDispatch}
            isLoading={isLoading}
            key={filterObject.id}
          />
        )
      })
    }
  }, [JSON.stringify(filtersData), reducerDispatch, isLoading, activeSearchElementKey])

  const filterElements = useMemo(() => {
    let filterElsArray: JSX.Element[] = []
    if (!groupInputs) {
      filtersData.forEach((filterObject, idx) => {
        // preserve the order of the filter elements by pushing them to the array in the order they are defined
        if (filterObject.type === 'select')
          filterElsArray.push((filterSelectElements as (JSX.Element | undefined)[])[idx] as JSX.Element)
        if (filterObject.type === 'text')
          filterElsArray.push((filterFreetextElements as (JSX.Element | undefined)[])[idx] as JSX.Element)
      })
    } else {
      filterElsArray = filterSelectElements.filter(filterEl => !!filterEl) as JSX.Element[]
      filterElsArray.push(filterFreetextElements as JSX.Element)
    }
    return filterElsArray
  }, [JSON.stringify(filtersData), reducerDispatch, isLoading, activeSearchElementKey])

  if (initialisation?.isInitialising)
    return (
      <FilterRowSkeleton count={initialisation?.skeletonCount || 4} className={clsx(styles.filter__row, className)} />
    )
  return (
    <div className={clsx(styles.filter__row, className)} data-testid={`filter-row-${filtersId}`}>
      {' '}
      {canGet && filterElements} {childElement}
    </div>
  )
}

export default FilterRow
